import { useState, useEffect } from 'react'
import { DocumentTextIcon, ArrowDownTrayIcon, CalendarIcon, PlusIcon } from '@heroicons/react/24/outline'
import { makeRequest } from 'services/request.service'
import { showAlert } from 'services/alerts.service'
import { useAuth } from 'auth/AuthContext'
import ButtonComponent from 'components/common/ButtonComponent'
import TableComponent from 'components/common/TableComponent'
import GenerateReportModal from 'components/modals/GenerateReportModal'

const Reports = () => {
  const { user } = useAuth()
  const [state, setState] = useState({
    reports: [],
    loading: true,
    isGenerateModalOpen: false
  })

  const fetchReports = async () => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: `/reports/${user.farmId}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        }
      })
      setState(s => ({
        ...s,
        reports: Array.isArray(response.data) ? response.data : [],
        loading: false
      }))
    } catch (err) {
      showAlert({ message: 'Failed to load reports', icon: 'error' })
      setState(s => ({ ...s, loading: false }))
    }
  }

  const handleDownloadReport = async (reportId, fileName) => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: `/reports/download/${reportId}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        },
        responseType: 'blob'
      })

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName || `report_${reportId}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      showAlert({ message: 'Report downloaded successfully', icon: 'success' })
    } catch (err) {
      showAlert({ message: 'Failed to download report', icon: 'error' })
    }
  }

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A'
    const kb = bytes / 1024
    const mb = kb / 1024
    return mb >= 1 ? `${mb.toFixed(2)} MB` : `${kb.toFixed(2)} KB`
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  useEffect(() => {
    if (user?.farmId) {
      fetchReports()
    }
  }, [user?.farmId])

  if (state.loading) {
    return (
      <div className='flex justify-center items-center h-64'>
        <div className='text-lg text-gray-600'>Loading reports...</div>
      </div>
    )
  }

  const tableData = {
    columns: [
      {
        key: 'reportName',
        label: 'Report Name',
        cell: (value) => (
          <div className='flex items-center'>
            <DocumentTextIcon className='w-5 h-5 text-gray-400 mr-3' />
            <span className='text-sm font-medium text-gray-900'>{value}</span>
          </div>
        )
      },
      {
        key: 'reportType',
        label: 'Type',
        cell: (value) => (
          <span className='px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800'>
            {value}
          </span>
        )
      },
      {
        key: 'fileSize',
        label: 'File Size',
        cell: (value) => formatFileSize(value)
      },
      {
        key: 'createdAt',
        label: 'Created At',
        cell: (value) => (
          <div className='flex items-center'>
            <CalendarIcon className='w-4 h-4 mr-2 text-gray-400' />
            <span>{formatDate(value)}</span>
          </div>
        )
      },
      {
        key: 'description',
        label: 'Description',
        cell: (value) => (
          <span className='max-w-xs truncate block'>{value || 'No description'}</span>
        )
      },
      {
        key: 'actions',
        label: 'Actions',
        cell: (value, row) => (
          <button
            onClick={() => handleDownloadReport(row.reportId, row.fileName)}
            className='inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors'
            title='Download Report'
          >
            <ArrowDownTrayIcon className='w-4 h-4' />
            Download
          </button>
        )
      }
    ],
    rows: state.reports
  }

  return (
    <div className='p-6'>
      {/* Header */}
      <div className='flex justify-between items-center mb-6'>
        <div className='flex items-center gap-3'>
          <DocumentTextIcon className='w-8 h-8 text-blue-600' />
          <div>
            <h1 className='text-2xl font-bold text-gray-800'>Reports Management</h1>
            <p className='text-sm text-gray-500 mt-1'>View and download generated reports</p>
          </div>
        </div>
        <ButtonComponent 
          onClick={() => setState(s => ({ ...s, isGenerateModalOpen: true }))}
        >
          <PlusIcon className='w-5 h-5' />
          <span className='font-medium'>Generate Report</span>
        </ButtonComponent>
      </div>

      {/* Reports Table */}
      {state.reports.length === 0 ? (
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 text-center py-12'>
          <DocumentTextIcon className='w-16 h-16 text-gray-400 mx-auto mb-4' />
          <p className='text-gray-500 text-lg'>No reports found</p>
          <p className='text-gray-400 text-sm mt-2'>Generate your first report to get started</p>
        </div>
      ) : (
        <TableComponent
          tableData={tableData}
          defaultSortColumn='createdAt'
        />
      )}

      {/* Generate Report Modal */}
      <GenerateReportModal
        isOpen={state.isGenerateModalOpen}
        onClose={() => setState(s => ({ ...s, isGenerateModalOpen: false }))}
        onGenerate={fetchReports}
      />
    </div>
  )
}

export default Reports
